/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useContext } from "react";
import { Row, Col } from "react-awesome-styled-grid";
import { Box, Text } from "@nordcloud/gnui";
import { FlexContainer } from "~/components";
import { PDServiceAdd } from "~/components/Applications/ApplicationDetails/PDServiceAdd";
import { PDServicesCustomerDropdown } from "~/components/Applications/ApplicationDetails/PDServicesCustomerDropdown";
import { PDServicesTable } from "~/components/Applications/ApplicationDetails/PDServicesTable";
import { DetailsTitle } from "~/components/Styled/Common";
import { usePDEscalationPolicies } from "~/hooks/usePDEscalationPolicies";
import { usePDServices } from "~/hooks/usePDServices";
import { ApplicationPDService } from "~/models";
import { ApplicationViewCtx } from "~/views/customer/applications/ApplicationViewCtx";

function PDServices() {
  const { application, updateApplication, canEdit } = useContext(
    ApplicationViewCtx
  );

  const { services, servicesLoading } = usePDServices(application.pdCustomer);
  const {
    escalationPolicies,
    escalationPoliciesLoading,
  } = usePDEscalationPolicies();

  const servicesNotAddedYet = services.filter(
    (service) =>
      !application.pdServices.some((s) => s.serviceId === service.pagerduty_id)
  );

  const addPDService = React.useCallback(
    (service: ApplicationPDService) =>
      updateApplication({ pdServices: [...application.pdServices, service] }),
    [application, updateApplication]
  );

  const removePDService = React.useCallback(
    (serviceId: string) =>
      updateApplication({
        pdServices: application.pdServices.filter(
          (s) => s.serviceId !== serviceId
        ),
      }),
    [application, updateApplication]
  );

  return (
    <Box spacing="spacing04">
      <Row>
        <Col lg={12}>
          <FlexContainer>
            <Text weight="medium">PagerDuty Services</Text>
            {application.pdCustomer ? (
              <PDServiceAdd
                addPDService={addPDService}
                services={servicesNotAddedYet}
                escalationPolicies={escalationPolicies}
              />
            ) : null}
          </FlexContainer>

          <FlexContainer align="center">
            <DetailsTitle>Customer:</DetailsTitle>
            <PDServicesCustomerDropdown
              application={application}
              updateApplication={updateApplication}
              canEdit={canEdit}
            />
          </FlexContainer>

          {application.pdCustomer ? (
            <PDServicesTable
              application={application}
              services={services}
              escalationPolicies={escalationPolicies}
              loading={servicesLoading || escalationPoliciesLoading}
              canEdit={canEdit}
              removeService={removePDService}
            />
          ) : null}
        </Col>
      </Row>
    </Box>
  );
}

export { PDServices };
